import { appMounted } from "@features/app/model";

import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import flagsmith, { IFlagsmith } from "flagsmith";
import { reset } from "patronum";

const initializeFlagsmith = createEvent<IFlagsmith>();
export const flagsUpdated = createEvent();
export const identify = createEvent<string | null>();

export const $flagsmith = createStore<IFlagsmith | null>(null).on(initializeFlagsmith, (_, instance) => instance);
export const $identificationCompleted = createStore(false);
const $pendingUserId = createStore<string | null>(null);

sample({
    clock: identify,
    source: $flagsmith,
    filter: (flagsmith, userId) => flagsmith === null && userId !== null,
    fn: (flagsmith, userId) => userId,
    target: $pendingUserId
});

const initFlagsmithFx = createEffect(async (): Promise<IFlagsmith> => {
    const envID = `${process.env.NEXT_PUBLIC_FLAGSMITH_KEY}`;

    await flagsmith.init({
        environmentID: envID,
        cacheFlags: false,
        enableAnalytics: true
    });
    return flagsmith;
});

export const identifyFx = createEffect(
    async ({ flagsmith, userId }: { flagsmith: IFlagsmith | null; userId: string }) => {
        await flagsmith!.identify(userId);
        return flagsmith;
    }
);

const getUserIdFx = createEffect(() => {
    if (typeof window === "undefined") return null;
    const id = localStorage.getItem("user_uuid");
    return id ? id : null;
});

// Flagsmith disabled: init not run; bypass below sets identificationCompleted and flagsUpdated
// sample({
//     clock: appMounted,
//     target: initFlagsmithFx
// });

// sample({
//     clock: initFlagsmithFx.doneData,
//     target: initializeFlagsmith
// });

sample({
    clock: appMounted,
    fn: () => true,
    target: $identificationCompleted
});
sample({
    clock: appMounted,
    target: flagsUpdated
});

sample({
    clock: initializeFlagsmith,
    target: flagsUpdated
});

sample({
    clock: initFlagsmithFx.doneData,
    target: getUserIdFx
});

sample({
    clock: getUserIdFx.doneData,
    filter: (userId) => userId !== null,
    target: identify
});

sample({
    clock: initializeFlagsmith,
    source: $pendingUserId,
    filter: (pendingUserId, flagsmith) => pendingUserId !== null,
    fn: (pendingUserId, flagsmith) => ({ flagsmith, userId: pendingUserId! }),
    target: identifyFx
});

sample({
    clock: identify,
    source: $flagsmith,
    filter: (flagsmith, userId) => flagsmith !== null && userId !== null,
    fn: (flagsmith, userId) => ({ flagsmith, userId: userId! }),
    target: identifyFx
});

sample({
    clock: identifyFx.doneData,
    fn: () => true,
    target: $identificationCompleted
});

sample({
    clock: identifyFx.doneData,
    target: flagsUpdated
});

reset({
    clock: identifyFx.done,
    target: $pendingUserId
});

const contract = new ModelContract({ $flagsmith, $identificationCompleted }, { identify, flagsUpdated }, {}, {});

export type IFlagsmithContract = typeof contract;

export class FlagsmithModule extends ModuleDescription<IFlagsmithContract> {
    getDefaultContract() {
        return contract;
    }

    run(): void {}
}

export function createFlagStore(flagName: string) {
    const $flag = createStore<any>(null);

    sample({
        source: $flagsmith,
        clock: [$flagsmith, flagsUpdated],
        filter: (flagsmith) => flagsmith !== null,
        fn: (flagsmith) => flagsmith!.hasFeature(flagName) && flagsmith!.getValue(flagName),
        target: $flag
    });

    return $flag;
}
